/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { Save } from "lucide-react";
import { useAdminGetSkill, useAdminUpdateSkill } from "@/services/skillService";



const initialState = {
    initialApiPayload: {
        name: '',
        code: '',
        active: true,
    },
    apiPayload: {
        name: '',
        code: '',
        active: true,
    },
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_INITIAL_API_PAYLOAD: 'setInitialApiPayload',
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_INITIAL_API_PAYLOAD:
            return { ...state, apiPayload: action.payload, initialApiPayload: action.payload, error_message: null }
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}

export default function Add() {

    const [ , setPageTitle,  , setShowBackButton] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()
    const location = useLocation()
    const {id} = useParams()

    const adminGetSkill = useAdminGetSkill()
    const adminUpdateSkill = useAdminUpdateSkill()


    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Modification d'un métier")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
        }
    }, [setPageTitle, setShowBackButton])



    /* On met à jour le titre du formulaire lorsque les informations du métier sont disponibles */
    useEffect(() => {
        if(state?.initialApiPayload?.name)
            setPageTitle(`Modification d'un métier : ${state.initialApiPayload.name}`)
    }, [setPageTitle, state.initialApiPayload])



    /* On récupère le métier automatiquement lorsque l'id est disponible */
    useEffect(() => {
        const getSkill = async () => {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const skill = await adminGetSkill(id)
                dispatch({ type: ACTIONS.SET_INITIAL_API_PAYLOAD, payload: skill })
            } catch (error) {
                console.error('admin/skills/[id]::getSkill::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            }
        }

        getSkill()
    }, [id])



    /* Gestion des changements de champs du formulaire */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
        dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, subtype: e.target.name, payload: e.target.type == 'checkbox' ? e.target.checked : e.target.value })
    }



    /* Soumission du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if(!state.apiPayload.name || !state.apiPayload.code ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return 
        }

        const payload = Object.fromEntries(Object.entries(state.apiPayload).filter(([key, value]) => {
            return value != state.initialApiPayload[key]
        }))

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminUpdateSkill(id, payload)
            navigate(location.state?.from ?? "/admin/skills")
        } catch (error) {
            console.error('admin/skills/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (code déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }





    return (
        <>
            <h2 className="text-secondary text-lg flex-none content-center mb-4">
                Paramètres du métier
            </h2>

            <form className="flex flex-col gap-4" onSubmit={(e) => handleSubmit(e)}>

                {/* Premiere ligne : code*/}
                <div className="md:w-[30%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="code">
                            Code <span className="text-red-500">*</span>
                        </label>
                        <input type="text" 
                            className={`w-full border ${ state?.apiPayload?.code != state?.initialApiPayload?.code ? "border-red-600" : "border-secondary" } rounded-md`}
                            id="code" name="code" 
                            value={state.apiPayload.code} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>
                {/* Deuxieme ligne : nom */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="name">
                            Nom <span className="text-red-500">*</span>
                        </label>
                        <input type="text" 
                            className={`w-full border ${ state?.apiPayload?.name != state?.initialApiPayload?.name ? "border-red-600" : "border-secondary" } rounded-md`}
                            id="name" name="name" 
                            value={state.apiPayload.name} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>

                {/* Troisième ligne : Actif */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        
                        <input type="checkbox" 
                            className={`border h-6 w-6 ${ state?.apiPayload?.active != state?.initialApiPayload?.active ? "border-red-600" : "border-secondary" } rounded-md`}
                            id="active" name="active" 
                            checked={state.apiPayload.active} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="active">
                            Actif
                        </label>
                    </div>
                </div>

                
                <div className="w-[50%] gap-5 flex flex-row items-center">
                    
                    <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                        onClick={(e) => handleSubmit(e)}
                        disabled={state.isLoading}
                    >
                        <Save size={24} className="text-primary" />
                        Sauvegarder
                    </button>
                    
                    {
                        state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                    }
                </div>
            </form>
        </>
    )
}